// reducers/quoteReducer.js
import { addDays } from 'date-fns';

// Action types
export const QUOTE_ACTIONS = {
    UPDATE_QUOTE_DATA: 'UPDATE_QUOTE_DATA',
    SET_CUSTOMER: 'SET_CUSTOMER',
    SET_ITEMS: 'SET_ITEMS',
    UPDATE_ITEM: 'UPDATE_ITEM',
    ADD_ITEM: 'ADD_ITEM',
    REMOVE_ITEM: 'REMOVE_ITEM',
    UPDATE_DELIVERY_OPTIONS: 'UPDATE_DELIVERY_OPTIONS',
    TOGGLE_PAYMENT_METHOD: 'TOGGLE_PAYMENT_METHOD',
    SET_TEMPLATE: 'SET_TEMPLATE',
    LOAD_QUOTE: 'LOAD_QUOTE',
    RESET: 'RESET'
};

// Initial state factory
export const createInitialState = (quoteId = null, isEditMode = false, invoiceSettings = {}, quoteSettings = {}) => {
    // Extract payment methods that are enabled from invoice settings (for UI compatibility)
    const enabledPaymentMethods = {};
    if (invoiceSettings?.paymentMethods) {
        const methods = ['bankgiro', 'plusgiro', 'paypal', 'swish'];
        methods.forEach(method => {
            enabledPaymentMethods[method] = invoiceSettings.paymentMethods[method] === true;
        });
    }

    console.log('Enabled payment methods:', enabledPaymentMethods);

    // Get default delivery method from invoice settings (for UI compatibility)
    const defaultDeliveryMethod = invoiceSettings?.deliveryChannels?.defaultChannel || 'download';

    // Get default VAT rate from invoice settings (for UI compatibility)
    const defaultVatRate = invoiceSettings?.taxSettings?.defaultVatRate || 25;

    // Get default validity days from QUOTE settings (this is quote-specific)
    const validityDays = quoteSettings?.defaultTerms?.validityDays || 30;

    // Get template from invoice settings (for UI compatibility)
    const template = invoiceSettings?.template?.defaultTemplate || 'classic';

    return {
        // Customer info
        customerid: '',
        customer: null,

        // Quote details
        quoteNumber: '',
        quoteDate: new Date(),
        validUntil: addDays(new Date(), validityDays),

        // Financial settings
        currency: 'SEK',
        roundTotal: false,
        defaultVatRate: defaultVatRate,

        // Content
        items: [],
        notes: '',
        attachments: [],

        // Status
        status: 'Draft',

        // Delivery options - initialized from settings
        deliveryOptions: {
            method: defaultDeliveryMethod,
            email: '',
            phone: '',
            message: '',
            subject: 'Offert'
        },

        // Payment methods - initialized from settings
        paymentMethods: enabledPaymentMethods,

        // Template - initialized from settings
        selectedTemplate: template,
    };
};

// Quote reducer
export const quoteReducer = (state, action) => {
    switch (action.type) {
        case QUOTE_ACTIONS.UPDATE_QUOTE_DATA:
            return {
                ...state,
                ...action.payload
            };

        case QUOTE_ACTIONS.SET_CUSTOMER:
            console.log('QUOTE REDUCER: Setting customer, current payment methods:', state.paymentMethods);
            const customerState = {
                ...state,
                customer: action.payload,
                customerid: action.payload?.id || '',
                // Update delivery email if customer has email, but preserve other delivery options
                deliveryOptions: {
                    ...state.deliveryOptions,
                    email: action.payload?.email || state.deliveryOptions.email
                }
                // DON'T touch payment methods here
            };
            console.log('QUOTE REDUCER: After setting customer, payment methods:', customerState.paymentMethods);
            return customerState;

        case QUOTE_ACTIONS.SET_ITEMS:
            return {
                ...state,
                items: action.payload
            };

        case QUOTE_ACTIONS.UPDATE_ITEM:
            return {
                ...state,
                items: state.items.map((item, idx) =>
                    idx === action.index ? action.payload : item
                )
            };

        case QUOTE_ACTIONS.ADD_ITEM:
            return {
                ...state,
                items: [...state.items, action.payload]
            };

        case QUOTE_ACTIONS.REMOVE_ITEM:
            return {
                ...state,
                items: state.items.filter((_, idx) => idx !== action.index)
            };

        case QUOTE_ACTIONS.UPDATE_DELIVERY_OPTIONS:
            return {
                ...state,
                deliveryOptions: {
                    ...state.deliveryOptions,
                    ...action.payload
                }
            };

        case QUOTE_ACTIONS.TOGGLE_PAYMENT_METHOD:
            return {
                ...state,
                paymentMethods: {
                    ...state.paymentMethods,
                    [action.payload]: !state.paymentMethods[action.payload]
                }
            };

        case QUOTE_ACTIONS.SET_TEMPLATE:
            return {
                ...state,
                selectedTemplate: action.payload
            };

        case QUOTE_ACTIONS.LOAD_QUOTE:
            // When loading existing quote, merge with current state
            const loadedQuote = action.payload;
            const quoteSettings = action.quoteSettings || {};

            console.log('QUOTE REDUCER: Loading quote with payment methods:', loadedQuote.paymentMethods);

            // Use quote settings for fallback validity days
            const defaultValidityDays = quoteSettings?.defaultTerms?.validityDays || 30;

            const newState = {
                ...state,
                customerid: loadedQuote.customerid || '',
                customer: loadedQuote.customer || null,
                quoteNumber: loadedQuote.quoteNumber || '',
                quoteDate: loadedQuote.quoteDate || new Date(),
                validUntil: loadedQuote.validUntil || addDays(new Date(), defaultValidityDays),
                currency: loadedQuote.currency || 'SEK',
                roundTotal: loadedQuote.roundTotal || false,
                items: loadedQuote.items || [],
                notes: loadedQuote.notes || '',
                attachments: loadedQuote.attachments || [],
                status: loadedQuote.status || 'Draft',
                deliveryOptions: {
                    ...state.deliveryOptions,
                    ...(loadedQuote.deliveryOptions || {})
                },
                // Use loaded payment methods completely, don't merge with state
                paymentMethods: loadedQuote.paymentMethods || {},
                selectedTemplate: loadedQuote.selectedTemplate || state.selectedTemplate
            };
            
            console.log('QUOTE REDUCER: New state payment methods:', newState.paymentMethods);
            return newState;

        case QUOTE_ACTIONS.RESET:
            return action.payload || createInitialState();

        default:
            return state;
    }
};